import { FC, useState } from 'react';
import { ColorPicker, useColor } from 'react-color-palette';
import 'react-color-palette/css';
import './react-color-palette.css';
import OutsideClickHandle from 'components/outside-click-handler/OutsideClickHandler';
import { CheckboxTickIcon, NavCloseIcon, WhitePencilIcon } from 'assets/icons';
import { twMerge } from 'tailwind-merge';

interface ColorPickerInputProps {
  color: string;
  onChange: (color: string) => void;
  label?: string;
  isMandatory?: boolean;
  showPresetColors?: boolean;
  presetColors?: string[];
  position?: 'top' | 'bottom';
  inputClassName?: string;
  showTick?: boolean;
  showSelectedColor?: boolean;
  customPresetColorsClassName?: string;
}

const DEFAULT_PRESET_COLORS = [
  '#007BFF',
  '#28A745',
  '#DC3545',
  '#FFC107',
  '#17A2B8',
  '#FD7E14',
  '#6F42C1',
  '#20C997',
  '#6C757D'
];

const ColorPickerInput: FC<ColorPickerInputProps> = ({
  color,
  onChange,
  label,
  isMandatory = false,
  showPresetColors = false,
  presetColors = DEFAULT_PRESET_COLORS,
  position = 'bottom',
  inputClassName,
  showTick = false,
  showSelectedColor = false,
  customPresetColorsClassName = ''
}) => {
  const [inputColor, setInputColor] = useColor(color);
  const [showPicker, setShowPicker] = useState(false);

  const hideColorPicker = () => {
    setShowPicker(false);
    onChange(inputColor.hex);
  };

  const handlePresetClick = (presetColor: string) => {
    setInputColor({ hex: presetColor, rgb: { r: 0, g: 0, b: 0, a: 1 }, hsv: { h: 0, s: 0, v: 0, a: 1 } });
    onChange(presetColor);
  };

  const closeColorPicker = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    hideColorPicker();
  };

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <div className="text-17px font-semibold leading-[18px] tracking-[-0.3px] text-headingGray">
          {label}
          {isMandatory && <span className="pl-0.5 text-error">*</span>}
        </div>
      )}
      {showPresetColors && (
        <div className={` my-4 flex flex-wrap gap-2 ${customPresetColorsClassName}`}>
          {showSelectedColor && !presetColors.includes(color) && (
            <div
              className={`flex h-10 w-10 shrink-0 cursor-pointer items-start justify-center rounded-full border-2 ${
                color === inputColor.hex ? 'border-secondary' : 'border-secondaryBtn'
              }`}
              style={{ backgroundColor: color }}
              onClick={() => handlePresetClick(color)}>
              {showTick && color === inputColor.hex && <CheckboxTickIcon className="m-auto h-5 w-5 " />}
            </div>
          )}
          {presetColors.map(presetColor => (
            <div
              key={presetColor}
              className={`flex h-10 w-10 shrink-0 cursor-pointer items-start justify-center rounded-full border-2 ${
                presetColor === inputColor.hex ? 'border-secondary' : 'border-secondaryBtn'
              }`}
              style={{ backgroundColor: presetColor }}
              onClick={() => handlePresetClick(presetColor)}>
              {showTick && presetColor === inputColor.hex && <CheckboxTickIcon className="m-auto h-5 w-5 " />}
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-3">
        <OutsideClickHandle onOutsideClick={hideColorPicker} disabled={!showPicker}>
          <div className="relative flex items-center" role="presentation" onClick={() => setShowPicker(true)}>
            <div className="flex cursor-pointer items-center rounded-l-md border border-borderGray bg-primary p-2">
              <div
                className="flex h-10 w-10 items-center justify-center rounded-full border border-border"
                style={{ backgroundColor: inputColor.hex }}>
                <WhitePencilIcon className="h-5 w-5 text-white" />
              </div>
            </div>
            <div
              className={twMerge(
                'w-32 rounded-r-md border border-l-0 border-borderGray bg-secondaryBtn py-[18px] pl-2 pr-8',
                inputClassName
              )}>
              <div className="ml-2 text-sbase font-medium text-primaryText">{inputColor.hex.toUpperCase()}</div>
            </div>
            {showPicker && (
              <div
                id="color-picker"
                className={`absolute ${position === 'top' ? 'bottom-full mb-3' : 'top-full mt-3'} left-0 z-10 w-64 
                rounded-[2px] border border-borderGray bg-primary`}>
                <div className="ml-auto w-fit cursor-pointer p-2" onClick={closeColorPicker}>
                  <NavCloseIcon className="h-2 w-2" />
                </div>
                <ColorPicker color={inputColor} onChange={setInputColor} hideInput hideAlpha />
              </div>
            )}
          </div>
        </OutsideClickHandle>
      </div>
    </div>
  );
};

export default ColorPickerInput;
